import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, tokenStore } from '../api/client';
import { ORG_KEY, ORG_ORDER } from '../config';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(Boolean(tokenStore.get()));
  const [org, setOrgState] = useState(sessionStorage.getItem(ORG_KEY) || '');

  const clear = useCallback(() => {
    tokenStore.clear();
    sessionStorage.removeItem(ORG_KEY);
    setUser(null);
    setOrgState('');
  }, []);

  useEffect(() => {
    if (!tokenStore.get()) return;
    api.get('auth/me/').then((res) => setUser(res.user)).catch(clear).finally(() => setBooting(false));
  }, [clear]);

  useEffect(() => {
    window.addEventListener('ats:unauthorized', clear);
    return () => window.removeEventListener('ats:unauthorized', clear);
  }, [clear]);

  const orgs = useMemo(() => (user ? ORG_ORDER.filter((o) => user.organizations.includes(o)) : []), [user]);
  const activeOrg = orgs.includes(org) ? org : orgs[0] || '';

  const value = useMemo(() => ({
    user,
    booting,
    orgs,
    org: activeOrg,
    setOrg: (next) => {
      sessionStorage.setItem(ORG_KEY, next);
      setOrgState(next);
    },
    hasPage: (page) => Boolean(user && user.pages.includes(page)),
    // Teams the user may filter by. [] means "no restriction" (all teams).
    teams: user && !user.teams.includes('*') ? user.teams : [],
    allTeams: Boolean(user && user.teams.includes('*')),
    login: async (email, password) => {
      const res = await api.post('auth/login/', { email, password });
      tokenStore.set(res.token);
      setUser(res.user);
      return res.user;
    },
    logout: async () => {
      try { await api.post('auth/logout/'); } catch (error) { /* token may already be invalid */ }
      clear();
    },
    refresh: async () => setUser((await api.get('auth/me/')).user),
  }), [user, booting, orgs, activeOrg, clear]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
